import { useEffect, useState } from "react";
import { ApiError } from "@/api/client";
import {
  useApplyBusinessMemory,
  useBusinessMemory,
  useDraftBusinessMemory,
  useModels,
  type BusinessMemoryDraft,
} from "@/api/queries";
import { useRunStream } from "@/chat/useRunStream";
import { Button } from "@/components/ui/Button";
import { Chip } from "@/components/ui/Chip";
import { Select } from "@/components/ui/Select";
import { Spinner } from "@/components/ui/Spinner";
import { Textarea } from "@/components/ui/Textarea";
import { AlertIcon, BookIcon, SparkIcon } from "@/components/icons";
import { cn } from "@/lib/cn";

/**
 * The Business Memory generator (PRD FR-2.3).
 *
 * One click runs the agent against the live schema and drafts a dictionary;
 * what comes back is shown as a **diff against what is already there**, with
 * Append and Replace as two deliberate acts. Nothing is ever auto-saved — the
 * field this writes into is the one the prompt presents to the model as
 * authoritative, so a generated dictionary that saved itself would be a model
 * writing its own instructions.
 *
 * The draft is editable before it is applied. A model that got seven sections
 * right and one wrong should cost a reviewer two minutes, not a retype.
 */

type Mode = "append" | "replace";

function DiffView({ diff }: { diff: string }) {
  const lines = diff.split("\n");
  return (
    <pre className="border-border bg-card rounded-element max-h-96 overflow-auto border p-3 type-code">
      {lines.map((line, index) => (
        <div
          key={index}
          className={cn(
            "whitespace-pre-wrap",
            line.startsWith("+") && !line.startsWith("+++") && "text-success",
            line.startsWith("-") && !line.startsWith("---") && "text-error",
            line.startsWith("@@") && "text-ink-disabled",
            (line.startsWith("+++") || line.startsWith("---")) && "text-ink-disabled",
          )}
        >
          {line || " "}
        </div>
      ))}
    </pre>
  );
}

function Preview({
  draft,
  mode,
  onMode,
  connectionId,
}: {
  draft: BusinessMemoryDraft;
  mode: Mode;
  onMode: (mode: Mode) => void;
  connectionId: string;
}) {
  const apply = useApplyBusinessMemory(connectionId);
  const [text, setText] = useState(draft.text);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    setText(draft.text);
    setEditing(false);
  }, [draft.message_id, draft.text]);

  const edited = text !== draft.text;
  const error = apply.error instanceof ApiError ? apply.error.message : null;

  return (
    <div className="border-border bg-surface rounded-container flex flex-col gap-4 border p-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="type-h4 text-ink">Proposed dictionary</span>
        {draft.model_label ? <Chip>{draft.model_label}</Chip> : null}
        <Chip tone="accent">
          {draft.queries} quer{draft.queries === 1 ? "y" : "ies"} run
        </Chip>
        <Chip tone="success">+{draft.added}</Chip>
        {draft.removed > 0 ? <Chip tone="error">−{draft.removed}</Chip> : null}
        {edited ? <Chip tone="warning">edited</Chip> : null}
      </div>

      {draft.missing_sections.length > 0 ? (
        <p className="type-support text-warning inline-flex items-start gap-2">
          <AlertIcon className="mt-0.5 size-4 shrink-0" />
          <span>
            This draft is missing {draft.missing_sections.length} of the sections asked for:{" "}
            {draft.missing_sections.join(", ")}. Small models lose long instructions — read it
            carefully, or draft again with a larger one.
          </span>
        </p>
      ) : null}
      {draft.error_code ? (
        <p className="type-support text-error">
          The run ended with {draft.error_code}: {draft.error_message}
        </p>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        {(["append", "replace"] as const).map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => onMode(option)}
            className={cn(
              "rounded-element border px-3 py-1.5 type-label motion-fast",
              mode === option
                ? "border-accent bg-accent-subtle text-accent"
                : "border-border text-ink-secondary hover:bg-hover",
            )}
          >
            {option === "append" ? "Append to the notes" : "Replace the notes"}
          </button>
        ))}
        <button
          type="button"
          onClick={() => setEditing((value) => !value)}
          className="type-support text-accent hover:underline"
        >
          {editing ? "Back to the diff" : "Edit before applying"}
        </button>
      </div>

      {editing ? (
        <Textarea
          label="Draft"
          hint="Correct anything the model got wrong. What you leave here is what gets saved."
          mono
          rows={20}
          spellCheck={false}
          value={text}
          onChange={(event) => setText(event.target.value)}
        />
      ) : edited ? (
        <p className="type-support text-ink-secondary">
          You have edited the draft. Switch to the editor to see it, or apply it as it stands.
        </p>
      ) : (
        <DiffView diff={draft.diff} />
      )}

      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="primary"
          type="button"
          loading={apply.isPending}
          onClick={() => apply.mutate({ mode, text: edited ? text : undefined })}
        >
          {mode === "append" ? "Append to the dictionary" : "Replace the dictionary"}
        </Button>
        {apply.isSuccess ? (
          <span className="type-support text-success">
            Saved. The change is in the audit log with its diff.
          </span>
        ) : null}
        {error ? <span className="type-support text-error">{error}</span> : null}
      </div>
    </div>
  );
}

export function BusinessMemoryPanel({ connectionId }: { connectionId: string }) {
  const [mode, setMode] = useState<Mode>("append");
  const [modelRef, setModelRef] = useState("");
  const [runId, setRunId] = useState<string | null>(null);
  const { data: models } = useModels();
  const { data: draft, isLoading, refetch } = useBusinessMemory(connectionId, mode);
  const start = useDraftBusinessMemory(connectionId);

  // The same stream a chat turn uses, so the schema sweep is visible while it
  // happens (PRD NFR-2) — this is the longest generation in the product. No
  // thread id: this run is not a conversation anybody is reading, so there is
  // no thread cache to invalidate when it finishes.
  const { run, busy } = useRunStream(runId, null);
  useEffect(() => {
    if (runId && (run.status === "complete" || run.status === "error")) {
      setRunId(null);
      void refetch();
    }
  }, [runId, run.status, refetch]);

  const running = busy || Boolean(runId) || start.isPending;
  const error = start.error instanceof ApiError ? start.error.message : null;

  return (
    <div className="flex flex-col gap-5">
      <div className="border-border bg-surface rounded-container border p-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="max-w-2xl">
            <span className="type-h4 text-ink inline-flex items-center gap-2">
              <SparkIcon className="size-4" />
              Draft business memory
            </span>
            <p className="type-support text-ink-secondary mt-2">
              The agent reads the schema — relations, definitions, and a few small SELECTs where
              a column's meaning is not obvious from its name — and writes the dictionary this
              source is missing. You review a diff before anything is saved.
            </p>
          </div>
          <Button
            variant="primary"
            type="button"
            loading={running}
            onClick={() =>
              start.mutate(modelRef || undefined, {
                onSuccess: (started) => setRunId(started.run_id),
              })
            }
          >
            {draft ? "Draft again" : "Draft it"}
          </Button>
        </div>

        <div className="mt-4 max-w-sm">
          <Select
            label="Model"
            value={modelRef}
            onChange={(event) => setModelRef(event.target.value)}
            hint="A long, structured generation. A larger model holds the eight sections more reliably than a small one."
          >
            <option value="">Use this source's usual model</option>
            {(models ?? []).map((model) => (
              <option key={model.ref} value={model.ref}>
                {model.provider_name} · {model.label}
              </option>
            ))}
          </Select>
        </div>
        {error ? <p className="type-support text-error mt-3">{error}</p> : null}
      </div>

      {running ? (
        <div className="border-border bg-surface rounded-container flex items-start gap-3 border p-4">
          <Spinner />
          <div className="min-w-0">
            <p className="type-label text-ink">{run.phase || "Starting…"}</p>
            {run.steps.length > 0 ? (
              <ul className="mt-2 flex flex-col gap-1">
                {run.steps.map((step, index) => (
                  <li key={index} className="type-support text-ink-secondary truncate">
                    {step.tool}
                    {step.sql ? ` · ${step.sql}` : ""}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        </div>
      ) : null}

      {isLoading ? (
        <div className="flex items-center gap-2">
          <Spinner />
          <span className="type-support text-ink-secondary">Loading the last draft…</span>
        </div>
      ) : draft ? (
        <Preview draft={draft} mode={mode} onMode={setMode} connectionId={connectionId} />
      ) : !running ? (
        <p className="type-support text-ink-secondary inline-flex items-center gap-2">
          <BookIcon className="size-4" />
          No draft yet. Nothing here writes the dictionary until you press Append or Replace.
        </p>
      ) : null}
    </div>
  );
}
